import { MicrophoneIcon, PhoneIcon } from '@heroicons/react/solid';

import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import type { DemoBrand } from '../types';
import { useChannelsT } from '../useChannelsT';
import { delay, tone } from '../../primitives';

/** Bar heights of the voice wave, in percent. */
const WAVE = [28, 54, 82, 46, 100, 64, 36, 72, 92, 50, 30, 60, 84, 42, 24];

/**
 * A visitor talking to the agent out loud in the website chat: the call
 * under way, what was said so far, and the plan it needs.
 */
export default function VoiceCall({
  brand,
  heard,
  said,
}: {
  brand: DemoBrand;
  /** What the visitor just said. */
  heard: string;
  /** What the agent is saying back. */
  said: string;
}) {
  const { t } = useChannelsT();

  return (
    <div className='relative mx-auto max-w-[30rem] pb-2 sm:pb-10'>
      <div className='mx-auto w-full overflow-hidden rounded-[22px] bg-white text-start shadow-[0_40px_90px_-30px_rgba(0,0,0,0.7)] ring-1 ring-white/10 sm:w-[19rem] dark:bg-[#1c1b27]'>
        <div
          className={clsxm(
            'flex items-center gap-2.5 px-4 py-3 text-white',
            brand.bg,
          )}
        >
          <span className='flex h-7 w-7 flex-none items-center justify-center rounded-full bg-white/20 text-[12px] font-semibold'>
            {brand.initial}
          </span>
          <span className='min-w-0 flex-1 truncate text-[13px] font-semibold'>
            {brand.name}
          </span>
          <span
            dir='ltr'
            className='flex-none text-[12px] tabular-nums text-white/80'
          >
            0:42
          </span>
        </div>
        <div className='flex flex-col items-center px-5 pb-5 pt-6'>
          <span className='relative flex h-20 w-20 items-center justify-center'>
            <span
              className={clsxm(
                'absolute inset-0 animate-ping rounded-full opacity-20 motion-reduce:animate-none',
                brand.bg,
              )}
            />
            <span
              className={clsxm(
                'relative flex h-16 w-16 items-center justify-center rounded-full text-[22px] font-semibold text-white',
                brand.bg,
              )}
            >
              {brand.initial}
            </span>
          </span>
          <div
            className={clsxm(
              styles.wave,
              styles.waveCenter,
              'mt-5 flex h-9 items-center gap-[3px]',
            )}
          >
            {WAVE.map((height, index) => (
              <span
                key={index}
                className={clsxm('w-[3px] rounded-full', brand.bg)}
                style={{ height: `${height}%`, ...delay(index * 70) }}
              />
            ))}
          </div>
          <p
            className={clsxm(
              'mt-4 text-center text-[12.5px] italic',
              tone.quiet,
            )}
          >
            “{heard}”
          </p>
          <p
            className={clsxm(
              'mt-2 text-center text-[13px] leading-snug',
              tone.ink,
            )}
          >
            {said}
          </p>
          <div className='mt-5 flex items-center gap-4'>
            <span className='flex h-11 w-11 items-center justify-center rounded-full bg-[#f1f1f5] text-[#56566d] dark:bg-white/10 dark:text-[#a4a4bd]'>
              <MicrophoneIcon className='h-5 w-5' />
            </span>
            <span className='flex h-11 w-11 items-center justify-center rounded-full bg-[#e5484d] text-white'>
              <PhoneIcon className='h-5 w-5 rotate-[135deg]' />
            </span>
          </div>
        </div>
      </div>

      <div
        className={clsxm(
          styles.rv,
          'relative mt-4 rounded-2xl p-4 text-start shadow-[0_30px_60px_-24px_rgba(0,0,0,0.65)] sm:absolute sm:bottom-2 sm:end-0 sm:mt-0 sm:w-[14.5rem]',
          tone.surface,
          tone.line,
        )}
        style={delay(450)}
      >
        <p className={clsxm('text-[13px] font-semibold', tone.ink)}>
          {t('Voice calls')}
        </p>
        <p className={clsxm('mt-1 text-xs leading-5', tone.body)}>
          {t(
            'In your website chat on Unlimited 200 and up. Try it in the Playground on any plan.',
          )}
        </p>
      </div>
    </div>
  );
}
